import { useMutation } from '@tanstack/react-query';
import { isEmpty, isNil } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { parseUrlParams } from '~frontend-common/helper/url/parse-url-params';
import { useHistory } from '~frontend-common/hooks/use-history';
import { useTranslator } from '~frontend-common/i18n/translator';
import { Box } from '~ui-kit/components/box/box';
import { Navbar } from '~web-portal/components/navbar';
import { authService } from '~web-portal/services';
import { Links } from '~web-portal/variables/links';
import { Paths } from '~web-portal/variables/paths';
import { HelpBox } from './components/help-box';
import { LoginForm } from './components/login-form';
import { OneTimePasscodeForm } from './components/one-time-passcode-form';

export function LoginPage() {
  function getPasscodeTokenFromUrl(search: string): string {
    const params = parseUrlParams(search);
    return params.get(Paths.login.urlParams.token);
  }

  function getEmailFromUrl(search: string): string {
    const params = parseUrlParams(search);
    const email = params.get(Paths.login.urlParams.email);
    return !isNil(email) ? decodeURIComponent(email) : undefined;
  }

  function getReturnUrlFromUrl(search: string): string {
    const params = parseUrlParams(search);
    const encodedReturnUrl = params.get(Paths.login.urlParams.returnUrl);
    return !isEmpty(encodedReturnUrl) ? decodeURIComponent(encodedReturnUrl) : undefined;
  }

  const { getCurrentLanguageId } = useTranslator();
  const history = useHistory();

  const [email, setEmail] = useState<string>(getEmailFromUrl(history.location.search));
  const [passcodeToken, setPasscodeToken] = useState<string>(getPasscodeTokenFromUrl(history.location.search));

  const initiateOtpFlowMutation = useMutation({
    mutationFn: async ({ email }: { email: string }) => {
      return authService.requestOneTimePasscode(email, getCurrentLanguageId());
    },
    onSuccess: (passcodeToken: string, { email }) => {
      let search = `${Paths.login.urlParams.token}=${passcodeToken}&${Paths.login.urlParams.email}=${encodeURIComponent(email)}`;

      // Keep the return url param before redirecting to the passcode form
      const returnUrl = getReturnUrlFromUrl(history.location.search);
      if (!isEmpty(returnUrl)) {
        search += `&${Paths.login.urlParams.returnUrl}=${encodeURIComponent(returnUrl)}`;
      }

      history.push({
        pathname: Paths.login.path,
        search,
      });
    },
  });

  useEffect(() => {
    const unsubscribe = history.subscribe((location) => {
      setEmail(getEmailFromUrl(location.search));
      setPasscodeToken(getPasscodeTokenFromUrl(location.search));
    });

    return () => unsubscribe();
  }, []);

  const handleEmailFilled = (email: string) => {
    setEmail(email);

    initiateOtpFlowMutation.mutate({ email });
  };

  const handleResendPasscode = () => {
    initiateOtpFlowMutation.mutate({ email });
  };

  function handleLoginSuccess() {
    const returnUrl = getReturnUrlFromUrl(history.location.search);
    history.push(returnUrl || Paths.policies);
  }

  return (
    <Box flexDirection="column" data-id="login-page" flex="1 1 auto">
      {/* This Helmet component adds a canonical link html tag to the <head> in the raw HTML. Used to improve SEO */}
      <Helmet>
        <link rel="canonical" href="portal.youset.ca/login" />
      </Helmet>

      <Navbar logoLink={{ path: Links.webPage, external: true, openNewTab: false }} isLoggedIn={false} />

      <Box flexDirection="column" alignItems="center" pt={[5, 7, 8]} flex="1 1 auto">
        <Box flexDirection="column" width={['100%', '450px']} px={[3]}>
          {email ? (
            <OneTimePasscodeForm
              email={email}
              passcodeToken={passcodeToken}
              onRequestNewPasscode={handleResendPasscode}
              onSuccess={handleLoginSuccess}
            />
          ) : (
            <LoginForm
              isLoading={initiateOtpFlowMutation.isLoading}
              error={initiateOtpFlowMutation.error}
              onEmailFilled={handleEmailFilled}
            />
          )}
        </Box>

        <HelpBox mt={5} mb={3} />
      </Box>
    </Box>
  );
}
